import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import {
	EditorState,
	DraftHandleValue,
	convertToRaw,
	convertFromRaw,
	RichUtils,
} from 'draft-js';
import Editor from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import 'draft-js/dist/Draft.css';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from '../icon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
const imagePlugin = createImagePlugin();
import './editable-text.scss';
import SelectNative from '../select/select-native';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../admin/create/session/editor/moduleControls/edit-section-colors-modal';
import { ColorKeys, TextOverrides } from '../../../types/working-model';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../store/types';
import { parseColor } from '../../admin/document-head';

interface EditableTextDraftProps {
	value: string;
	placeholder?: string;
	className?: string;
	onChange: (text: string) => void;
	style?: React.CSSProperties;
	elementName?: EContentColorOverrideNames;
	onOverrideChange?: (override: TextOverrides) => void;
	colorOverride?: string
	defaultColor?: ColorKeys;
	disabled?: boolean;
}

const EditableTextDraft: React.FC<EditableTextDraftProps> = (props: EditableTextDraftProps) => {
	const {
		colorOverride,
		onOverrideChange,
		defaultColor,
		elementName,
		value,
		className,
		style,
		onChange,
		placeholder,
		disabled
	} = props;
	const {
		CreateEventReducer: {
			workingEvent
		}
	} = useSelector((state: EventsState) => state);

	const editor = useRef<any>(null);
	const currentContent = useRef<any>(null);
	const [editorState, setEditorState] = useState(value ? EditorState.createWithContent(convertFromRaw(markdownToDraft(value as string))) : EditorState.createEmpty());
	const [active, setActive] = useState(false);
	const [activeStates, setActiveStates] = useState<string[]>([]);
	const [activeBlockType, setActiveBlockType] = useState('paragraph');
	const colorPalette = workingEvent?.settings.design.colors.colors;
	const color = colorOverride?.split("-")[0];
	const [colorsOpen, setColorsOpen] = useState(false);

	function focusEditor() {
		editor.current?.focus();
	}

	useEffect(() => {
		if (value) {
			setEditorState(EditorState.createWithContent(convertFromRaw(markdownToDraft(value as string))));
		}
	}, [value]);

	useEffect(() => {
		currentContent.current = editorState;
	}, [editorState]);

	function onChangeEditor(editorState: EditorState) {
		const activeStates = Array.from(editorState.getCurrentInlineStyle().values() as any) as string[];
		setActiveStates(activeStates as string[]);
		setEditorState(editorState);
		setActiveBlockType(editorState.getCurrentContent().getBlockForKey(editorState.getSelection().getFocusKey()).getType());
	}


	function undo(e: any) {
		e.preventDefault();
		setEditorState(EditorState.undo(editorState));
	}

	function redo() {
		setEditorState(EditorState.redo(editorState));
	}

	function toggleSize(type: string) {
		setTimeout(focusEditor, 16);
		onChangeEditor(RichUtils.toggleBlockType(editorState, type));
	}

	function toggleUL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'unordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	function toggleOL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'ordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	function handleFontStyle(e: any, style: number | string): DraftHandleValue {
		//cmd + B/I/U will send a string as e instead of click event
		if (typeof e !== "string") {
			e.preventDefault();
		}

		const newState = RichUtils.toggleInlineStyle(editorState, String(style));

		if (activeStates.includes(String(style))) {
			setActiveStates(activeStates.filter((st: string) => st !== String(style)));
		}

		if (newState) {
			setEditorState(newState);
			setTimeout(focusEditor, 16);
			return 'handled';
		}

		return 'not-handled';
	}

	function handleColorChange(color: string) {
		if (!onOverrideChange) return;
		onOverrideChange({
			color
		});
	}

	const startingColor = color || defaultColor;
	const currentColor: [string, number] | undefined = useMemo(() => startingColor ? colorPalette?.[startingColor] : ['#444', 1], [startingColor, colorPalette]);


	// const parsedStyle = useMemo(() => {
	// 	if (style) {
	// 		return {
	// 			...style,
	// 			color: currentColor ? parseColor(currentColor) : startingColor
	// 		};
	// 	} else {
	// 		return {
	// 			color: currentColor ? parseColor(currentColor) : startingColor
	// 		};
	// 	}
	// }, [style, currentColor, startingColor]);

	useEffect(() => {
		function checkClick(e: any) {
			const path = e.path || e.composedPath();
			for (const item of path) {
				if (item.id === 'editable-text') {
					return;
				}
			}

			onChange(draftToMarkdown(convertToRaw(editorState.getCurrentContent())));
			setActive(false);
		}

		if (active)
			window.addEventListener('mousedown', checkClick);

		return () => {
			window.removeEventListener('mousedown', checkClick);
		};
	}, [active, editorState, onChange]);

	return (
		<div className={classNames("editable-text", { active, disabled }, className)} style={style} id="editable-text">
			<div className={classNames(colorOverride)}>
				<Editor
					ref={editor}
					editorState={editorState}
					onChange={onChangeEditor}
					plugins={[imagePlugin]}
					onFocus={() => setActive(true)}
					placeholder={placeholder}
				/>
			</div>
			<button className="round edit-text-button" onClick={() => editor.current?.focus()}><Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} /></button>
			<div className="toolbar">
				<SelectNative
					options={[
						{
							label: "Heading 1",
							value: 'header-one'
						},
						{
							label: "Heading 2",
							value: 'header-two'
						},
						{
							label: "Heading 3",
							value: 'header-three'
						},
						{
							label: "Heading 4",
							value: 'header-four'
						},
						{
							label: "Heading 5",
							value: 'header-five'
						},
						{
							label: "Paragraph",
							value: 'paragraph'
						},
						{
							label: "Unstyled",
							value: 'unstyled'
						}
					]}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) => toggleSize(e.target.value)}
					value={activeBlockType}
				/>
				{startingColor ? (
					<button
						className={classNames("small round color-display")}
						style={{
							backgroundColor: currentColor ? parseColor(currentColor) : "",
						}}
						onClick={() => setColorsOpen(true)}
					/>
				) : null}
				<div className="editor-controls-container editor-center">
					<button className={classNames("no-cancel bold", { active: activeStates.includes('BOLD') })} onMouseDown={(e) => handleFontStyle(e, 'BOLD')} title="bold"><Icon name="bold" size={20} color={COLORS.GRAY} viewBox={"-3 0 17 14"} /></button>
					<button className={classNames("no-cancel italic", { active: activeStates.includes('ITALIC') })} onMouseDown={(e) => handleFontStyle(e, 'ITALIC')} title="italic"><Icon name="italic" size={20} color={COLORS.GRAY} viewBox={"-4 0 17 14"} /></button>
					{/* FUTURE DEV: removing underline ability until functional */}
					{/* <button className={classNames("no-cancel underline", { active: activeStates.includes('UNDERLINE') })} onMouseDown={(e) => handleFontStyle(e, 'UNDERLINE')} title="underline"><Icon name="underline" size={20} color={COLORS.GRAY} viewBox={"-3 -2 17 19"} /></button> */}
				</div>
				<div className="editor-controls-container editor-center">
					<button className="ol no-cancel" onMouseDown={toggleOL} title="list"><Icon name="ol" size={17} color={COLORS.GRAY} viewBox={'0 -1 17 17'} /></button>
					<button className="ul no-cancel" onMouseDown={toggleUL} title="bullets"><Icon name="ul" size={17} color={COLORS.GRAY} viewBox={'0 -1 17 17'} /></button>
				</div>
				<div className="editor-controls-container editor-right">
					<button className="undo no-cancel" onClick={undo}><Icon name={ICONS.UNDO_ALT} color={COLORS.GRAY} size={20} /></button>
					<button className="redo no-cancel" onClick={redo}><Icon name={ICONS.REDO_ALT} color={COLORS.GRAY} size={20} /></button>
				</div>
			</div>

			<EditSectionColorsModal
				open={colorsOpen}
				onClose={() => setColorsOpen(false)}
				onFinish={handleColorChange}
				title={elementName || EContentColorOverrideNames.title}
			/>
		</div>
	);
};

export default EditableTextDraft;